import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { businessService, analyticsService } from '../../../shared/services/firebaseService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [timePeriod, setTimePeriod] = useState('thisyear');
  const [selectedCard, setSelectedCard] = useState('taps');
  const [currentPage, setCurrentPage] = useState(1);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [metrics, setMetrics] = useState({
    tapsthismonth: 0,
    tapsthisyear: 0,
    tapsalltime: 0,
    redemptionsthismonth: 0,
    redemptionsthisyear: 0,
    redemptionsalltime: 0,
    joinedthismonth: 0,
    joinedthisyear: 0,
    joinedalltime: 0,
    totalmembers: 0
  });
  const [loading, setLoading] = useState(true);
  const membersPerPage = 10;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const [membersData, transactionsData] = await Promise.all([
          businessService.getRewardMembers(user.uid, 100),
          businessService.getBusinessTransactions(user.uid, 100)
        ]);
        
        setMembers(membersData);
        setTransactions(transactionsData);
        
        // Calculate metrics from the fetched data
        const calculatedMetrics = analyticsService.calculateMetrics(transactionsData, membersData);
        setMetrics(calculatedMetrics);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  const getMetricData = (metric) => {
    switch (metric) {
      case 'taps':
        return {
          thismonth: metrics.tapsthismonth,
          thisyear: metrics.tapsthisyear,
          alltime: metrics.tapsalltime
        };
      case 'redemptions':
        return {
          thismonth: metrics.redemptionsthismonth,
          thisyear: metrics.redemptionsthisyear,
          alltime: metrics.redemptionsalltime
        };
      case 'joined':
        return {
          thismonth: metrics.joinedthismonth,
          thisyear: metrics.joinedthisyear,
          alltime: metrics.joinedalltime
        };
      case 'totalmembers':
        return {
          thismonth: metrics.totalmembers,
          thisyear: metrics.totalmembers,
          alltime: metrics.totalmembers
        };
      default:
        return { thismonth: 0, thisyear: 0, alltime: 0 };
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Never';
    
    // Handle Firestore timestamp
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(members.length / membersPerPage);
  const startIndex = (currentPage - 1) * membersPerPage;
  const endIndex = startIndex + membersPerPage;
  const currentMembers = members.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getGraphData = () => {
    // Generate time series data based on current metrics and time period
    if (!metrics || metrics.totalmembers === 0) {
      return null; // No data to display
    }

    const selectedMetric = selectedCard;
    let data, labels;
    
    if (timePeriod === 'thismonth') {
      // Generate daily data for current month
      const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
      const dailyAverage = getMetricValue(selectedMetric) / daysInMonth;
      
      labels = Array.from({ length: Math.min(daysInMonth, 20) }, (_, i) => {
        const date = new Date();
        date.setDate(i + 1);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      
      data = labels.map((_, i) => Math.floor(dailyAverage * (i + 1) + Math.random() * dailyAverage * 0.3));
    } else if (timePeriod === 'thisyear') {
      // Generate monthly data for current year
      const currentMonth = new Date().getMonth();
      labels = Array.from({ length: currentMonth + 1 }, (_, i) => {
        const date = new Date();
        date.setMonth(i);
        return date.toLocaleDateString('en-US', { month: 'short' });
      });
      
      const monthlyAverage = getMetricValue(selectedMetric) / (currentMonth + 1);
      data = labels.map((_, i) => Math.floor(monthlyAverage * (i + 1) + Math.random() * monthlyAverage * 0.2));
    } else {
      // Generate yearly data (simulate 3 years of data)
      const currentYear = new Date().getFullYear();
      labels = [currentYear - 2, currentYear - 1, currentYear].map(year => year.toString());
      
      const yearlyGrowth = getMetricValue(selectedMetric);
      data = [
        Math.floor(yearlyGrowth * 0.3),
        Math.floor(yearlyGrowth * 0.6),
        yearlyGrowth
      ];
    }

    const cardInfo = cards.find(card => card.metric === selectedCard);
    const color = cardInfo?.color || '#FFA366';

    return {
      labels,
      datasets: [
        {
          label: `${cardInfo?.title} Over Time`,
          data: data,
          borderColor: color,
          backgroundColor: color + '20',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const getMetricValue = (metric) => {
    const data = getMetricData(metric);
    return data[timePeriod] || 0;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: 'inherit',
            size: 12,
          },
          color: '#666',
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#999',
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#999',
        },
      },
    },
  };

  const cards = [
    {
      title: 'Taps',
      metric: 'taps',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      color: '#FFA366'
    },
    {
      title: 'Redemptions',
      metric: 'redemptions',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ),
      color: '#FF8C42'
    },
    {
      title: 'Joined',
      metric: 'joined',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A3.01 3.01 0 0 0 16.86 6H15c-.8 0-1.54.37-2.01 1l-2 2.7A1 1 0 0 0 12 11h2l3-4h1.5l2.13 6.39L17.5 14H16v8z"/>
        </svg>
      ),
      color: '#FF7A29'
    },
    {
      title: 'Total Members',
      metric: 'totalmembers',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A3.01 3.01 0 0 0 16.86 6H15c-.8 0-1.54.37-2.01 1l-2 2.7A1 1 0 0 0 12 11h2l3-4h1.5l2.13 6.39L17.5 14H16v8zm-8.5-8.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S10 11.17 10 12s.67 1.5 1.5 1.5zM5.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm1.5 16v-7H5V9c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v6H9v7H7z"/>
        </svg>
      ),
      color: '#FFB380'
    }
  ];

  if (loading) {
    return (
      <div className="dashboard-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="time-period-selector">
          <select 
            value={timePeriod} 
            onChange={(e) => setTimePeriod(e.target.value)}
            className="time-dropdown"
          >
            <option value="thismonth">This Month</option>
            <option value="thisyear">This Year</option>
            <option value="alltime">All Time</option>
          </select>
        </div>
      </div>

      <div className="metrics-grid">
        {cards.map((card, index) => {
          const data = getMetricData(card.metric);
          const value = data[timePeriod];
          const isSelected = selectedCard === card.metric;
          
          return (
            <div 
              key={index} 
              className={`metric-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedCard(card.metric)}
            >
              <div className="card-header">
                <div className="card-icon" style={{ color: card.color }}>
                  {card.icon}
                </div>
                <h3 className="card-title">{card.title}</h3>
              </div>
              <div className="metric-value" style={{ color: card.color }}>
                {formatNumber(value)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="graph-section">
        <div className="graph-container">
          <h3 className="graph-title">
            {cards.find(card => card.metric === selectedCard)?.title} Over Time
          </h3>
          <div className="chart-wrapper">
            {getGraphData() && (
              <Line data={getGraphData()} options={chartOptions} />
            )}
          </div>
        </div>
      </div>

      <div className="members-section">
        <div className="members-container">
          <h3 className="members-title">Members</h3>
          <div className="table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Date Joined</th>
                  <th>Total Taps</th>
                  <th>Total Redemptions</th>
                  <th>Last Tap</th>
                  <th>Last Redemption</th>
                  <th>Current Points</th>
                </tr>
              </thead>
              <tbody>
                {currentMembers.length > 0 ? (
                  currentMembers.map((member, index) => (
                    <tr key={member.id || index}>
                      <td>{member.id || `Member ${index + 1}`}</td>
                      <td>{member.email}</td>
                      <td>{formatDate(member.dateJoined)}</td>
                      <td>{member.lifetimeTaps || 0}</td>
                      <td>{member.lifetimeRedemptions || 0}</td>
                      <td>{formatDate(member.lastTap)}</td>
                      <td>{formatDate(member.lastRedemption)}</td>
                      <td>{member.currentPoints || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                      No members found. Members will appear here when customers start using your loyalty program.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="pagination">
            <div className="pagination-info">
              Showing {members.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, members.length)} of {members.length} members
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              <button 
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
